/**
 * User Measurement Service
 *
 * Service for managing user body measurements including logging,
 * retrieval, and image attachments. Extends the base CrudService
 * for measurement-specific operations with validation.
 */

import { ZodError } from 'zod';

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { ApiErrorClass } from '../types/api/errors';
import { API_ENDPOINTS } from '../types/api.types';
import { dayjs, formatLaravelDate } from '../utils/date';
import '../utils/string-extensions';
import {
  validateUserMeasurement,
  type UserMeasurementInput,
} from '../validation/user-measurement.schemas';

import type { GetApiResponse } from '../types/api.types';
import type { ID } from '../types/common/id.types';
import type {
  BodyMeasurementKey,
  MeasurementMetricDetail,
  UserMeasurementQueryParams,
  UserMeasurements,
  UserMeasurement,
} from '../types/domain/user-measurement.types';

/**
 * A read refused because the user keeps their measurements private (403).
 * Not a fault: callers read it as "private" (`classifyMeasurementsFailure`),
 * so it is rethrown but not logged — a console.error would show the dev
 * LogBox and report an error on every visit to a private client.
 */
const isPrivacyRefusal = (error: unknown): boolean =>
  error instanceof ApiErrorClass && error.status === 403;

// =============================================================================
// USER MEASUREMENT SERVICE CLASS
// =============================================================================

export class UserMeasurementService extends CrudService<
  UserMeasurement,
  UserMeasurements
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/user-measurement',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
      cacheVariesByUnit: true, // body measurements arrive in the user's unit
    });
  }

  // ---------------------------------------------------------------------------
  // CORE MEASUREMENT OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get measurement history for a given user.
   *
   * @param username - Username of the user
   * @param params - Optional query params (including `measurement`)
   */
  public async getUserMeasurements(
    username: string,
    params?: UserMeasurementQueryParams
  ): Promise<UserMeasurements[]> {
    if (!username) {
      throw new Error('Username is required to fetch user measurements');
    }

    try {
      return await this.withContext({
        template: '/user/{username}/user-measurement',
        parameters: { username },
        cacheNamespace: `${username}-measurements`,
      }).getAll(params);
    } catch (error) {
      if (!isPrivacyRefusal(error)) {
        console.error(
          `Failed to fetch user measurements for ${username}:`,
          error
        );
      }

      rethrowTyped(
        error,
        'Unable to fetch user measurements. Please try again.'
      );
    }
  }

  /**
   * One metric's whole history for a user, plus the metric's identity, the
   * goal tracking it and the metrics worth overlaying on it.
   *
   * Not composed from `getUserMeasurements`: the index's `measurement` filter
   * unsets the metrics blob, which is exactly the sibling data the detail page
   * is built on. Readings arrive oldest-first and already converted.
   */
  public async getMeasurementMetric(
    username: string,
    slug: BodyMeasurementKey
  ): Promise<MeasurementMetricDetail> {
    if (!username) {
      throw new Error('Username is required to fetch a measurement metric');
    }
    if (!slug) {
      throw new Error('A metric slug is required');
    }

    try {
      // getById would build this URL correctly, but it is typed to the
      // service's TDetail (a measurement entry) and this is a different shape.
      const response = await apiClient.get<
        GetApiResponse<MeasurementMetricDetail>
      >(`/user/${username}/user-measurement/metric/${slug}`);

      if (response.data?.data) {
        return response.data.data;
      }

      throw new Error(`No history returned for ${slug}`);
    } catch (error) {
      if (!isPrivacyRefusal(error)) {
        console.error(`Failed to fetch measurement metric ${slug}:`, error);
      }

      rethrowTyped(error, 'Unable to load this measurement. Please try again.');
    }
  }

  /**
   * One entry, with its photos. Always fetched fresh, never from the cache:
   * each photo's signed URL stops working 3 minutes after it is issued, so a
   * cached entry would hand back dead links. Fetch again for fresh ones.
   */
  public async getMeasurementById(id: ID): Promise<UserMeasurement> {
    if (!id) {
      throw new Error('Measurement ID is required');
    }

    try {
      const response = await apiClient.get<GetApiResponse<UserMeasurement>>(
        `/user-measurement/${id}`
      );
      const measurement = response.data?.data;
      if (!measurement) {
        throw new Error(`Measurement ${id} not found`);
      }
      return this.transformResponse(measurement);
    } catch (error) {
      if (!isPrivacyRefusal(error)) {
        console.error(`Failed to fetch user measurement ${id}:`, error);
      }
      rethrowTyped(
        error,
        'Unable to fetch user measurement details. Please check the ID and try again.'
      );
    }
  }

  /**
   * Create a new measurement log for a user.
   *
   * Uses userMeasurementSchema for client-side validation.
   *
   * The entry is dated `logged_on`: the device's own calendar day, read when
   * it is sent. Never a form field, and never the server's day — stamped on
   * the server's UTC clock, an evening entry landed on tomorrow. The API
   * stores it as sent. Create only: editing an entry does not move its day.
   *
   * @param username - Username of the user
   * @param data - Measurement payload
   */
  public async createUserMeasurement(
    username: string,
    data: UserMeasurementInput
  ): Promise<UserMeasurements> {
    if (!username) {
      throw new Error('Username is required to create a user measurement');
    }

    try {
      this.validateMeasurementData(data);

      const transformedData = {
        ...this.transformRequest(data),
        logged_on: formatLaravelDate(dayjs()),
      };

      return await this.withContext({
        template: `/user/{username}/user-measurement`,
        parameters: { username },
        cacheNamespace: `${username}-measurements`,
      }).create(transformedData);
    } catch (error) {
      console.error('Failed to create user measurement:', error);
      rethrowTyped(
        error,
        'Unable to create user measurement. Please check your data and try again.'
      );
    }
  }

  /**
   * Update an existing measurement log by ID.
   *
   * Uses userMeasurementSchema for client-side validation.
   *
   * @param id - Measurement ID
   * @param data - Updated measurement payload
   */
  public async updateUserMeasurement(
    id: ID,
    data: UserMeasurementInput
  ): Promise<UserMeasurements> {
    if (!id) {
      throw new Error(
        'Measurement ID is required to update a user measurement'
      );
    }

    try {
      this.validateMeasurementData(data);

      const transformed = this.transformRequest(data);
      const updated = await this.update(id, transformed, { partial: true });

      return updated;
    } catch (error) {
      console.error(`Failed to update user measurement ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to update user measurement. Please check your data and try again.'
      );
    }
  }

  /**
   * Delete a measurement log by ID.
   *
   * @param id - Measurement ID
   */
  public async deleteUserMeasurement(id: ID): Promise<void> {
    if (!id) {
      throw new Error(
        'Measurement ID is required to delete a user measurement'
      );
    }

    try {
      await this.delete(id);
    } catch (error) {
      console.error(`Failed to delete user measurement ${id}:`, error);

      rethrowTyped(
        error,
        'Unable to delete user measurement. Please try again.'
      );
    }
  }

  /**
   * Delete one saved photo from an entry: the record and its stored file. The
   * owner only, on any plan, with or without two-factor. It can't be undone.
   *
   * A 404 means the photo is already gone (removed elsewhere, or never on this
   * entry); callers treat that as done and refetch the entry.
   *
   * @param measurementId - The entry's id
   * @param imageId - An `id` from the entry's `images[]`
   */
  public async removeImage(measurementId: ID, imageId: ID): Promise<void> {
    if (!measurementId || !imageId) {
      throw new Error('A measurement and an image are required');
    }

    try {
      await apiClient.delete(
        API_ENDPOINTS.MEASUREMENT_IMAGE_DELETE.path.replaceParam({
          measurement: measurementId,
          image: imageId,
        })
      );
    } catch (error) {
      console.error(
        `Failed to remove image ${imageId} from measurement ${measurementId}:`,
        error
      );
      rethrowTyped(error, 'Couldn’t delete the photo. Try again.');
    } finally {
      // The list's images_count is now one lower, whichever way it went: a
      // 404 means the photo was already gone.
      this.invalidateCache(
        ['getAll', 'getPaginated', 'getById', 'search'],
        measurementId
      );
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION
  // ---------------------------------------------------------------------------

  /**
   * Validate measurement payload using Zod schema.
   */
  protected validateMeasurementData(data: UserMeasurementInput): void {
    try {
      validateUserMeasurement(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't save the measurement", error, {
          originalData: data,
          details: { service: 'UserMeasurementService' },
        });
      }

      throw new InvalidDataError(
        "Couldn't save the measurement: the data is in an unexpected format",
        {
          originalData: data,
          details: { service: 'UserMeasurementService' },
        }
      );
    }
  }

  /**
   * Transform request payload before sending to API.
   * Hook for converting data formats if needed.
   */
  protected override transformRequest(
    data: Partial<UserMeasurementInput>
  ): Partial<UserMeasurementInput> {
    return {
      ...data,
    };
  }

  /**
   * Normalize response from API for frontend consumption.
   */
  protected override transformResponse(data: UserMeasurement): UserMeasurement {
    return {
      ...data,
      metrics: data.metrics ?? [],
      images: data.images ?? [],
    };
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default user measurement service instance.
 */
export const userMeasurementService = new UserMeasurementService();
